import { useState } from 'react';
import { motion } from 'framer-motion';
import { useLang } from '@/context/LanguageContext';
import { UI_TEXT } from '@/data/tradeData';

const projects = [
  { before: '/portfolio/house1-before.jpeg', after: '/portfolio/house1-after.png', en: 'Complete Home Renovation', es: 'Renovación Completa', cat: { en: 'General Contracting', es: 'Contratación General' } },
  { before: '/portfolio/house2-before.jpg', after: '/portfolio/house2-after.jpg', en: 'Modern Luxury Remodel', es: 'Remodelación de Lujo', cat: { en: 'Remodeling', es: 'Remodelación' } },
  { before: '/portfolio/house3-before.jpeg', after: '/portfolio/house3-after.jpg', en: 'Full Property Transformation', es: 'Transformación Completa', cat: { en: 'General Remodeling', es: 'Remodelación General' } },
  { before: '/portfolio/house4-before.jpg', after: '/portfolio/house4-after.jpg', en: 'Interior Renovation', es: 'Renovación Interior', cat: { en: 'Remodeling', es: 'Remodelación' } },
  { before: '/portfolio/house5-before.jpg', after: '/portfolio/house5-after.jpg', en: 'Kitchen & Interior Upgrade', es: 'Cocina e Interior', cat: { en: 'Remodeling', es: 'Remodelación' } },
  { before: '/portfolio/house6-before.jpg', after: '/portfolio/house6-after.jpg', en: 'Ground-Up Custom Build', es: 'Construcción Desde Cero', cat: { en: 'New Construction', es: 'Construcción Nueva' } },
];

function Slider({ before, after, title, category, index }) {
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const handleMove = (e) => {
    if (!dragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPos((x / rect.width) * 100);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay: index * 0.1 }} data-testid={`portfolio-item-${index}`}>
      <div className="relative w-full h-[300px] sm:h-[400px] overflow-hidden cursor-ew-resize select-none bg-[#141414]"
        onMouseMove={handleMove} onMouseDown={() => setDragging(true)} onMouseUp={() => setDragging(false)} onMouseLeave={() => setDragging(false)}
        onTouchMove={handleMove} onTouchStart={() => setDragging(true)} onTouchEnd={() => setDragging(false)}>
        <img src={after} alt={`${title} — After`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img src={before} alt={`${title} — Before`} className="absolute inset-0 w-full h-full object-cover" style={{ minWidth: '100%', width: `${100 / (pos / 100)}%`, maxWidth: 'none' }} loading="lazy" />
        </div>
        <div className="absolute top-0 bottom-0 w-[2px] bg-[#E31937] z-10" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7 4L3 10L7 16" stroke="#E31937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M13 4L17 10L13 16" stroke="#E31937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
        </div>
        <span className="absolute top-3 left-3 z-20 bg-black/70 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1">Before</span>
        <span className="absolute top-3 right-3 z-20 bg-[#E31937] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1">After</span>
      </div>
      <div className="mt-3 flex items-baseline justify-between">
        <h3 className="font-display font-semibold text-white text-sm">{title}</h3>
        <span className="text-[10px] uppercase tracking-widest text-[#E31937] font-bold">{category}</span>
      </div>
    </motion.div>
  );
}

export default function Portfolio({ items }) {
  const { lang } = useLang();
  const t = UI_TEXT[lang].portfolio;
  const displayItems = items || projects;

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-12">
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#E31937] mb-4">{t.label}</p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white max-w-xl">{t.title}</h2>
          <p className="text-sm text-[#A3A3A3] mt-2">{t.drag}</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {displayItems.map((p, i) => (
            <Slider key={i} before={p.before} after={p.after} title={p[lang] || p.en} category={p.cat ? p.cat[lang] : ''} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export { Slider };
